import { supabase } from '@/lib/supabase'
import type { Produit, ProduitInsert, ProduitUpdate } from '@/types/database-real'
import type { ProduitFilters } from '@/types/produit'
import { qontoApiCall, getQontoStatus } from '@/services/qontoApi'

// ============================================
// GET ALL PRODUITS
// ============================================
export async function getProduits(filters?: ProduitFilters) {
  let query = supabase
    .from('produits')
    .select('*')

  // Search filter
  if (filters?.search) {
    query = query.or(`nom.ilike.%${filters.search}%,description.ilike.%${filters.search}%,code.ilike.%${filters.search}%`)
  }

  // Pagination
  if (filters?.page && filters?.limit) {
    const from = (filters.page - 1) * filters.limit
    const to = from + filters.limit - 1
    query = query.range(from, to)
  }

  const { data, error, count } = await query

  if (error) {
    console.error('Error fetching produits:', error)
    throw error
  }

  // Tri par code (en gérant les produits sans code)
  const sortedData = (data as Produit[]).sort((a, b) => {
    // Les produits avec code en premier
    if (a.code && !b.code) return -1
    if (!a.code && b.code) return 1

    // Si les deux ont un code, tri alphabétique
    if (a.code && b.code) {
      return a.code.localeCompare(b.code, 'fr', { numeric: true, sensitivity: 'base' })
    }

    // Si aucun n'a de code, tri par nom
    return a.nom.localeCompare(b.nom, 'fr')
  })

  return { data: sortedData, count }
}

// ============================================
// GET PRODUIT BY ID
// ============================================
export async function getProduitById(id: string) {
  const { data, error } = await supabase
    .from('produits')
    .select('*')
    .eq('id', id)
    .single()

  if (error) {
    console.error('Error fetching produit:', error)
    throw error
  }

  return data as Produit
}

// ============================================
// CREATE PRODUIT
// ============================================
export async function createProduit(produit: ProduitInsert) {
  const { data, error } = await supabase
    .from('produits')
    .insert(produit)
    .select()
    .single()

  if (error) {
    console.error('Error creating produit:', error)
    throw error
  }

  return data as Produit
}

// ============================================
// UPDATE PRODUIT
// ============================================
export async function updateProduit(id: string, produit: ProduitUpdate) {
  const { data, error } = await supabase
    .from('produits')
    .update({
      ...produit,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('Error updating produit:', error)
    throw error
  }

  return data as Produit
}

// ============================================
// DELETE PRODUIT
// ============================================
export async function deleteProduit(id: string) {
  const { error } = await supabase
    .from('produits')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting produit:', error)
    throw error
  }

  return true
}

// ============================================
// COUNT PRODUITS
// ============================================
export async function countProduits() {
  const { count, error } = await supabase
    .from('produits')
    .select('*', { count: 'exact', head: true })

  if (error) {
    console.error('Error counting produits:', error)
    throw error
  }

  return count || 0
}

// ============================================
// SYNC PRODUITS → QONTO (/v2/products)
// ============================================

interface QontoProduct {
  id: string
  title: string
  type: string
  unit_price: { value: string; currency: string }
  vat_rate: string
  description?: string | null
  unit?: string
}

export interface SyncResult {
  created: number
  updated: number
  deleted: number
  errors: string[]
}

/**
 * Synchronise tous les produits BDD vers Qonto /v2/products.
 * - Produits sans qonto_id → POST /products (CREATE)
 * - Produits avec qonto_id → DELETE + POST (re-create, pas de PATCH dispo)
 * - Products Qonto sans produit local → DELETE /products/:id
 */
export async function syncProduitsToQonto(): Promise<SyncResult> {
  const result: SyncResult = { created: 0, updated: 0, deleted: 0, errors: [] }

  // Vérifier connexion Qonto
  const status = await getQontoStatus()
  if (!status.connected) {
    throw new Error('Qonto non connecté. Veuillez vous connecter dans les paramètres.')
  }

  // 1. Récupérer tous les produits locaux
  const { data: localProduits, error } = await supabase
    .from('produits')
    .select('*')
    .order('code')

  if (error) throw error
  const produits = localProduits as Produit[]

  // 2. Récupérer tous les products Qonto existants (paginé)
  let qontoProducts: QontoProduct[] = []
  try {
    let page = 1
    let hasMore = true
    while (hasMore) {
      const res = await qontoApiCall<{ products: QontoProduct[]; meta: { current_page: number; total_pages: number } }>('/products', {
        params: { per_page: '100', page: String(page) },
      })
      const items = res?.products || []
      qontoProducts = [...qontoProducts, ...items]
      hasMore = (res?.meta?.current_page || 0) < (res?.meta?.total_pages || 0)
      page++
    }
  } catch (e) {
    console.warn('Impossible de lister les products Qonto:', e)
  }

  // Index des products Qonto par ID pour détecter les orphelins
  const qontoProductsById = new Map(qontoProducts.map(p => [p.id, p]))

  // 3. CREATE ou UPDATE (delete+create) chaque produit local
  for (const produit of produits) {
    const body = {
      title: produit.nom.substring(0, 120),
      type: 'service' as const,
      description: (produit.description || '').substring(0, 600) || undefined,
      unit_price: { value: String(produit.prix_unitaire), currency: produit.devise || 'EUR' },
      vat_rate: String((produit.tva ?? 0) / 100),
      unit: 'piece',
    }

    try {
      if (produit.qonto_id) {
        // UPDATE = delete ancien + create nouveau
        qontoProductsById.delete(produit.qonto_id)
        try {
          await qontoApiCall(`/products/${produit.qonto_id}`, { method: 'DELETE' })
        } catch {
          // Ignore si déjà supprimé
        }
        const res = await qontoApiCall<{ product: { id: string } }>('/products', {
          method: 'POST',
          body,
        })
        if (res?.product?.id) {
          await supabase
            .from('produits')
            .update({ qonto_id: res.product.id } as any)
            .eq('id', produit.id)
          result.updated++
        }
      } else {
        // CREATE
        const res = await qontoApiCall<{ product: { id: string } }>('/products', {
          method: 'POST',
          body,
        })
        if (res?.product?.id) {
          await supabase
            .from('produits')
            .update({ qonto_id: res.product.id } as any)
            .eq('id', produit.id)
          result.created++
        }
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      result.errors.push(`${produit.code || produit.nom}: ${msg}`)
    }
  }

  // 4. DELETE les products Qonto qui n'existent plus localement
  for (const [qontoId] of qontoProductsById) {
    try {
      await qontoApiCall(`/products/${qontoId}`, { method: 'DELETE' })
      result.deleted++
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      result.errors.push(`Suppression ${qontoId}: ${msg}`)
    }
  }

  return result
}
